/**
 * Vocabulário do domínio.
 *
 * Fonte única da verdade para enums usados por API, banco e interface.
 * Ao adicionar um valor aqui, verifique também:
 *   - packages/db/src/schema  (constraint da coluna)
 *   - design.md §3            (cor e ícone correspondentes)
 */

// ─────────────────────────────────────────────────────────── papéis

export const ROLES = ['owner', 'admin', 'gestor', 'fiscal', 'leitor'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  gestor: 'Gestor',
  fiscal: 'Fiscal',
  leitor: 'Leitor',
};

/** Hierarquia para comparação. Maior número = mais poder. */
export const ROLE_RANK: Record<Role, number> = {
  leitor: 0,
  fiscal: 1,
  gestor: 2,
  admin: 3,
  owner: 4,
};

export function roleAtLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/**
 * Senha que o gestor entrega ao cadastrar alguém ou ao redefinir.
 *
 * Não há envio de e-mail até a Fase 6, então o gestor precisa dizer a senha em
 * voz alta ou por mensagem — e uma senha aleatória de vinte caracteres não se
 * dita. Fica uma só, conhecida, curta de falar.
 *
 * **É uma senha pública, e de propósito.** Não protege nada: quem souber o
 * padrão a tentará em qualquer conta nova. Quem protege é a troca obrigatória
 * no primeiro acesso — enquanto ela não acontece, a conta só alcança a própria
 * rota de troca de senha, e nada mais (`mustChangePassword`).
 *
 * Por isso ela nunca passa pelas regras de senha escolhida: seria reprovada por
 * todas elas, e é essa a intenção. {@link MIN_PASSWORD_LENGTH} e a checagem de
 * vazamento valem para o que a pessoa escolhe, não para o que ela recebe.
 */
export const DEFAULT_PASSWORD = 'senha123';

/**
 * Tamanho mínimo da senha escolhida pela pessoa.
 *
 * Oito é o piso; o que sustenta a regra não é o comprimento e sim a recusa de
 * senha que já apareceu em vazamento público, conferida no servidor a cada
 * troca. Comprimento sozinho aprova "12345678"; a lista de vazamento não.
 */
export const MIN_PASSWORD_LENGTH = 8;

// ────────────────────────────────────────────────────── permissões

/**
 * O que uma pessoa alcança **dentro de um órgão**.
 *
 * O papel diz sobre quem ela pode agir; a permissão diz onde ela entra e o que
 * altera. Ficam separados porque um gestor de um município pode precisar ver
 * auditoria e o de outro não — sem que isso mude a hierarquia entre eles.
 */
export const PERMISSION_KEYS = [
  'canCreateObras',
  'canEditObras',
  'canEditAnyObra',
  'canDeleteObras',
  'canManageObraTeam',
  'canDeleteReports',
  'canAccessTasks',
  'canManageCompanies',
  'canDeleteCompanies',
  'canAccessSecretarias',
  'canAccessAudit',
  'canAccessReports',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export type Permissions = Record<PermissionKey, boolean>;

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  canCreateObras: 'Cadastrar obras',
  canEditObras: 'Editar obras',
  canEditAnyObra: 'Editar obras: alcance global',
  canDeleteObras: 'Excluir obras',
  canManageObraTeam: 'Gerir equipes de obra',
  canDeleteReports: 'Corrigir relatórios de obra',
  canAccessTasks: 'Acessar tarefas',
  canManageCompanies: 'Gerenciar empresas',
  canDeleteCompanies: 'Excluir empresas',
  canAccessSecretarias: 'Gerenciar secretarias',
  canAccessAudit: 'Acessar auditoria',
  canAccessReports: 'Acessar relatórios do sistema',
};

export const PERMISSION_HINTS: Record<PermissionKey, string> = {
  canCreateObras: 'Registrar obra nova.',
  canEditObras: 'Alterar obra já cadastrada. O alcance é escolhido abaixo.',
  canEditAnyObra: 'Qualquer obra do órgão, mesmo sem fazer parte dela.',
  canDeleteObras: 'Remover obra das listagens. A exclusão é lógica.',
  canManageObraTeam:
    'Incluir e retirar pessoas da equipe de qualquer obra e designar o responsável.',
  canDeleteReports:
    'Editar ou remover relatório já publicado, em qualquer obra, com justificativa. O fiscal responsável corrige os da própria obra sem esta caixinha. Quem corrige também enxerga o que foi removido.',
  canAccessTasks: 'Quadro de tarefas da equipe.',
  canManageCompanies: 'Cadastrar e editar empresas contratadas, com consulta de CNPJ.',
  canDeleteCompanies:
    'Remover empresa das listagens. Recusado enquanto houver obra vinculada; a exclusão é lógica.',
  canAccessSecretarias: 'Cadastrar e editar as secretarias demandantes.',
  canAccessAudit: 'Trilha de quem fez o quê.',
  canAccessReports: 'Relatórios consolidados do órgão.',
};

/**
 * Como a tela de usuários agrupa as caixas: o que se faz na obra, e o resto.
 *
 * `canEditAnyObra` não aparece aqui de propósito: é a sub-opção da caixinha
 * "Editar obras" (ver `PERMISSION_SUBOPTIONS`), desenhada dentro dela.
 */
export const PERMISSION_GROUPS: readonly {
  readonly title: string;
  readonly keys: readonly PermissionKey[];
}[] = [
  {
    title: 'Obras',
    keys: [
      'canCreateObras',
      'canEditObras',
      'canDeleteObras',
      'canManageObraTeam',
      'canDeleteReports',
    ],
  },
  {
    title: 'Cadastros e páginas',
    keys: [
      'canAccessTasks',
      'canManageCompanies',
      'canDeleteCompanies',
      'canAccessSecretarias',
      'canAccessAudit',
      'canAccessReports',
    ],
  },
];

/** Chaves que a tela desenha dentro de outra caixinha, e não como caixa própria. */
export const PERMISSION_SUBOPTIONS: readonly PermissionKey[] = ['canEditAnyObra'];

export const NO_PERMISSIONS: Permissions = {
  canCreateObras: false,
  canEditObras: false,
  canEditAnyObra: false,
  canDeleteObras: false,
  canManageObraTeam: false,
  canDeleteReports: false,
  canAccessTasks: false,
  canManageCompanies: false,
  canDeleteCompanies: false,
  canAccessSecretarias: false,
  canAccessAudit: false,
  canAccessReports: false,
};

export const ALL_PERMISSIONS: Permissions = {
  canCreateObras: true,
  canEditObras: true,
  canEditAnyObra: true,
  canDeleteObras: true,
  canManageObraTeam: true,
  canDeleteReports: true,
  canAccessTasks: true,
  canManageCompanies: true,
  canDeleteCompanies: true,
  canAccessSecretarias: true,
  canAccessAudit: true,
  canAccessReports: true,
};

/**
 * Padrão de um usuário recém-criado, conforme o papel (ADR 046).
 *
 * É padrão, não regra: toda caixinha continua na tela, para ligar ou desligar
 * caso a caso. Gestor nasce com tudo; fiscal com o que precisa para trabalhar
 * a obra — cadastrar obra, editar as próprias (alcance padrão), tarefas e a
 * contratada que ainda não existe; leitor só com o que se lê, auditoria e
 * relatórios do sistema. `owner` e `admin` não consultam caixinha (ver
 * `effectivePermissions`).
 */
const ROLE_DEFAULTS: Record<Role, Permissions> = {
  owner: ALL_PERMISSIONS,
  admin: ALL_PERMISSIONS,
  gestor: ALL_PERMISSIONS,
  fiscal: {
    ...NO_PERMISSIONS,
    canCreateObras: true,
    canEditObras: true,
    canAccessTasks: true,
    canManageCompanies: true,
  },
  leitor: { ...NO_PERMISSIONS, canAccessAudit: true, canAccessReports: true },
};

export function defaultPermissionsFor(role: Role): Permissions {
  return { ...ROLE_DEFAULTS[role] };
}

// ─────────────────────────────────────────── alcance de "Editar obras"

/**
 * Até onde a caixinha "Editar obras" alcança (ADR 052).
 *
 * **Padrão** é a obra da pessoa: a que ela cadastrou, pela qual responde ou de
 * cuja equipe faz parte. **Global** é qualquer obra do órgão, mesmo sem fazer
 * parte dela — o caso do gestor que corrige um contrato digitado por outro
 * setor. É uma sub-opção, e não uma segunda caixinha, porque global sem a
 * caixinha marcada não quer dizer nada.
 */
export const OBRA_EDIT_SCOPES = ['padrao', 'global'] as const;
export type ObraEditScope = (typeof OBRA_EDIT_SCOPES)[number];

export const OBRA_EDIT_SCOPE_LABELS: Record<ObraEditScope, string> = {
  padrao: 'Padrão',
  global: 'Global',
};

export const OBRA_EDIT_SCOPE_HINTS: Record<ObraEditScope, string> = {
  padrao: 'Só as obras que cadastrou, pelas quais responde ou de cuja equipe faz parte.',
  global: 'Qualquer obra do órgão, mesmo sem fazer parte dela.',
};

export function obraEditScope(permissions: Permissions): ObraEditScope {
  return permissions.canEditObras && permissions.canEditAnyObra ? 'global' : 'padrao';
}

/** Liga a caixinha com o alcance escolhido. */
export function withObraEditScope(permissions: Permissions, scope: ObraEditScope): Permissions {
  return { ...permissions, canEditObras: true, canEditAnyObra: scope === 'global' };
}

/**
 * Desfaz o estado que a tela não oferece: global com a caixinha desligada.
 *
 * Aplicado ao gravar, para que o que está no banco seja sempre um dos três
 * estados que a tela mostra — desligada, padrão ou global.
 */
export function normalizePermissions(permissions: Permissions): Permissions {
  if (permissions.canEditObras || !permissions.canEditAnyObra) return { ...permissions };
  return { ...permissions, canEditAnyObra: false };
}

/** O que a pessoa alcança antes de olhar qual obra é. */
export type ObraEditReach = 'nenhuma' | 'proprias' | 'todas';

export function obraEditReach(permissions: Permissions): ObraEditReach {
  if (!permissions.canEditObras) return 'nenhuma';
  return permissions.canEditAnyObra ? 'todas' : 'proprias';
}

/**
 * Quem pode editar **esta** obra.
 *
 * Sem a caixinha, ninguém — nem quem cadastrou. Com ela no alcance padrão, a
 * obra precisa ser da pessoa: cadastrada por ela, sob a responsabilidade dela
 * ou com ela na equipe. No alcance global, qualquer uma. O papel não entra:
 * `owner` e `admin` já chegam aqui com tudo marcado (`effectivePermissions`),
 * e abaixo deles vale o que a tela de usuários mostra.
 */
export function canEditObra(
  viewer: { readonly userId: string; readonly permissions: Permissions },
  obra: {
    readonly createdById: string | null;
    readonly responsibleFiscalId: string | null;
    readonly teamUserIds: readonly string[];
  },
): boolean {
  const reach = obraEditReach(viewer.permissions);
  if (reach === 'nenhuma') return false;
  if (reach === 'todas') return true;

  return (
    obra.createdById === viewer.userId ||
    obra.responsibleFiscalId === viewer.userId ||
    obra.teamUserIds.includes(viewer.userId)
  );
}

/**
 * Quem corrige os relatórios já publicados **desta** obra: edita ou remove
 * (ADR 060).
 *
 * O fiscal responsável pela obra corrige os relatórios dela, sem caixinha — é
 * ele quem responde pelo que foi registrado ali. Fora isso, a caixinha
 * "Corrigir relatórios de obra" vale para qualquer obra do órgão; o gestor nasce
 * com ela. `owner` e `admin` já chegam com tudo marcado (`effectivePermissions`),
 * e o leitor, que nunca escreve, é barrado antes, pelo papel.
 */
export function canCorrectReports(
  viewer: { readonly userId: string; readonly permissions: Permissions },
  obra: { readonly responsibleFiscalId: string | null },
): boolean {
  return viewer.permissions.canDeleteReports || obra.responsibleFiscalId === viewer.userId;
}

/**
 * Permissão que vale de fato.
 *
 * Quem administra o órgão alcança tudo dentro dele — marcar caixinha para
 * `owner` e `admin` só criaria o estado absurdo de um administrador sem acesso
 * a uma página que ele mesmo pode liberar para os outros. Abaixo deles vale o
 * que está gravado: o papel dá o padrão no cadastro (`defaultPermissionsFor`)
 * e nada mais, para que o que a tela mostra seja o que o servidor aplica.
 */
export function effectivePermissions(role: Role, stored: Permissions): Permissions {
  if (roleAtLeast(role, 'admin')) return ALL_PERMISSIONS;
  return { ...stored };
}

// ─────────────────────────────────────────────────────────── obra

/**
 * Situações da obra.
 *
 * "Planejada" saiu: o sistema acompanha obra que existe em campo, e uma obra
 * que ainda não começou não tem visita a vencer, medição a lançar nem foto a
 * publicar — só ocupava uma opção em todo seletor. Nenhuma obra estava nesse
 * estado quando ele foi removido.
 */
export const OBRA_STATUSES = ['em_andamento', 'paralisada', 'concluida', 'cancelada'] as const;
export type ObraStatus = (typeof OBRA_STATUSES)[number];

export const OBRA_STATUS_LABELS: Record<ObraStatus, string> = {
  em_andamento: 'Em andamento',
  paralisada: 'Paralisada',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

/** Token de cor de design.md §3. Não usar hex fora dos tokens. */
export const OBRA_STATUS_TONE: Record<ObraStatus, string> = {
  em_andamento: 'brand',
  paralisada: 'paused',
  concluida: 'success',
  cancelada: 'danger',
};

// ─────────────────────────────────────────────────── tipo de relatório

export const REPORT_TYPES = [
  'visita',
  'medicao',
  'aditivo_valor',
  'supressao_valor',
  'aditivo_prazo_os',
  'aditivo_prazo_contrato',
  'glosa',
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

/**
 * Ordem de exibição na tela de criação de relatório.
 * Definida pelo cliente — ver projeto_sistema_obras.md §4.4. Não reordenar.
 */
export const REPORT_TYPE_ORDER: readonly ReportType[] = [
  'visita',
  'medicao',
  'aditivo_valor',
  'supressao_valor',
  'aditivo_prazo_os',
  'aditivo_prazo_contrato',
  'glosa',
];

export interface ReportTypeMeta {
  readonly label: string;
  readonly hint: string;
  /** Token de cor (design.md §3). */
  readonly tone: 'muted' | 'success' | 'info' | 'danger' | 'warning';
  /** Ícone Lucide (design.md §6). */
  readonly icon: string;
  /** Como afeta o livro-razão. */
  readonly effect: 'none' | 'paid' | 'contract_value' | 'contract_term' | 'os_term' | 'gloss';
  /** Sinal aplicado ao valor lançado no razão. */
  readonly sign: -1 | 0 | 1;
  /** Exige `movement_date` além de `visit_date`. */
  readonly requiresMovementDate: boolean;
}

export const REPORT_TYPE_META: Record<ReportType, ReportTypeMeta> = {
  visita: {
    label: 'Visita técnica',
    hint: 'Sem alteração financeira',
    tone: 'muted',
    icon: 'ClipboardCheck',
    effect: 'none',
    sign: 0,
    requiresMovementDate: false,
  },
  medicao: {
    label: 'Medição',
    hint: 'Pagamento parcial',
    tone: 'success',
    icon: 'TrendingUp',
    effect: 'paid',
    sign: 1,
    requiresMovementDate: true,
  },
  aditivo_valor: {
    label: 'Aditivo · Valor',
    hint: 'Aumento de valor do contrato',
    tone: 'info',
    icon: 'Plus',
    effect: 'contract_value',
    sign: 1,
    requiresMovementDate: true,
  },
  supressao_valor: {
    label: 'Supressão',
    hint: 'Redução de valor do contrato',
    tone: 'danger',
    icon: 'Minus',
    effect: 'contract_value',
    sign: -1,
    requiresMovementDate: true,
  },
  aditivo_prazo_os: {
    label: 'Aditivo · OS',
    hint: 'Aumento do prazo da OS',
    tone: 'info',
    icon: 'CalendarPlus',
    effect: 'os_term',
    sign: 1,
    requiresMovementDate: true,
  },
  aditivo_prazo_contrato: {
    label: 'Aditivo · Contrato',
    hint: 'Aumento do prazo do contrato',
    tone: 'info',
    icon: 'CalendarPlus',
    effect: 'contract_term',
    sign: 1,
    requiresMovementDate: true,
  },
  glosa: {
    label: 'Glosa',
    hint: 'Saldo não pago',
    tone: 'warning',
    icon: 'CircleAlert',
    effect: 'gloss',
    sign: 1,
    requiresMovementDate: true,
  },
};

export const REPORT_STATUSES = ['rascunho', 'publicado'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

// ────────────────────────────────────────────── documentos da obra

export const DOCUMENT_CATEGORIES = [
  'contrato',
  'aditivo',
  'art_rrt',
  'projeto',
  'licenca',
  'ordem_servico',
  'edital',
  'planilha',
  'medicao',
  'outro',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  contrato: 'Contrato',
  aditivo: 'Termo aditivo',
  art_rrt: 'ART / RRT',
  projeto: 'Projeto',
  licenca: 'Licença',
  ordem_servico: 'Ordem de serviço',
  edital: 'Edital',
  planilha: 'Planilha orçamentária',
  medicao: 'Boletim de medição',
  outro: 'Outro',
};

// ─────────────────────────────────────────────────────────── tarefas

export const TASK_PRIORITIES = ['baixa', 'normal', 'alta', 'urgente'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

// ────────────────────────────────────────────────── prazo de visita

/** Padrão do sistema quando a organização não define outro. */
export const DEFAULT_VISIT_INTERVAL_DAYS = 15;

/** Dias de antecedência em que a visita passa a ser sinalizada como "vencendo". */
export const VISIT_DUE_SOON_DAYS = 3;

/**
 * `nao_se_aplica` é obra encerrada — concluída ou cancelada.
 *
 * Não é "em dia" nem "sem visita": é obra que não tem próxima visita a
 * acontecer. Antes, uma obra concluída em março continuava acumulando dias de
 * atraso e aparecendo no alerta de visitas vencidas, cobrando de um fiscal uma
 * vistoria em canteiro que não existe mais.
 */
export const VISIT_STATUSES = [
  'em_dia',
  'vencendo',
  'vencida',
  'sem_visita',
  'nao_se_aplica',
] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = {
  em_dia: 'Em dia',
  vencendo: 'Vencendo',
  vencida: 'Vencida',
  sem_visita: 'Sem visita',
  nao_se_aplica: 'Obra encerrada',
};

/** Obra encerrada: não recebe mais visita, e portanto não tem prazo. */
export function obraEncerrada(status: ObraStatus): boolean {
  return status === 'concluida' || status === 'cancelada';
}

/**
 * Pendências pelas quais a listagem filtra.
 *
 * São os motivos do painel menos a paralisação, que já é uma situação da obra
 * e entra pelo filtro de situação. Oferecê-la duas vezes faria a pessoa
 * perguntar qual das duas vale.
 */
export const OBRA_PENDENCIAS = [
  'visita_vencida',
  'visita_vencendo',
  'sem_visita',
  'contrato_vencido',
  'atrasada',
  'sem_fiscal',
] as const;
export type ObraPendencia = (typeof OBRA_PENDENCIAS)[number];

export const OBRA_PENDENCIA_LABELS: Record<ObraPendencia, string> = {
  visita_vencida: 'Visita vencida',
  visita_vencendo: 'Visita vencendo',
  sem_visita: 'Sem visita',
  contrato_vencido: 'Contrato vencido',
  atrasada: 'Execução atrasada',
  sem_fiscal: 'Sem fiscal',
};

// ─────────────────────────────────────────────────────────── tarefas

/**
 * A situação da tarefa — ADR 063.
 *
 * Quatro, iguais em todo o órgão e na ordem do trabalho: o que ainda vai ser
 * feito, o que está sendo feito, o que parou à espera de alguém ou de alguma
 * coisa, e o que acabou. "Aguardando" é a que o quadro antigo não tinha e a
 * que o dia a dia de um órgão público mais pede: documento da empresa,
 * assinatura, parecer.
 */
export const TASK_STATUSES = ['a_fazer', 'em_andamento', 'aguardando', 'concluida'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  a_fazer: 'A fazer',
  em_andamento: 'Em andamento',
  aguardando: 'Aguardando',
  concluida: 'Concluída',
};

/**
 * Prioridade da tarefa (no banco, `urgency`).
 *
 * `normal` é o silêncio: tarefa normal não ganha etiqueta nenhuma na lista.
 * Marcar o esperado faria a exceção desaparecer no meio — a mesma razão pela
 * qual obra em andamento não ganha selo.
 */
export const TASK_URGENCIES = ['baixa', 'normal', 'alta', 'urgente'] as const;
export type TaskUrgency = (typeof TASK_URGENCIES)[number];

export const TASK_URGENCY_LABELS: Record<TaskUrgency, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

/*
 * Legado do quadro Kanban (ADR 063): as etapas do quadro Pessoal e a paleta de
 * etiqueta. Não aparecem mais na tela; ficam só porque as colunas `stage` e
 * `color` continuam no banco, com as suas restrições, até a limpeza.
 */
export const TASK_STAGES = ['a_fazer', 'em_andamento', 'concluido'] as const;
export const TASK_COLORS = ['laranja', 'ambar', 'verde', 'azul', 'roxo', 'rosa'] as const;

// ─────────────────────────────────────────────────────────── mídia

export const MEDIA_OWNER_TYPES = [
  'obra_cover',
  'report',
  'obra_document',
  'task',
  'comment',
  'user_avatar',
  'org_logo',
] as const;
export type MediaOwnerType = (typeof MEDIA_OWNER_TYPES)[number];

/*
 * Não há mais um limite fixo por arquivo aqui. O corte é o do órgão
 * (`maxUploadBytes`, em `uploads.ts`), limitado pelo teto absoluto do servidor
 * (`MAX_UPLOAD_LIMIT_BYTES`). O teto fixo de 20 MB que morava aqui era aplicado
 * antes de a política ser lida, e o limite configurado acima dele nunca valia.
 */

export const ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ALLOWED_DOCUMENT_MIMES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/msword',
] as const;
